import { computed, ref, watch } from 'vue';
import NucleoWorkOrdersAPI from 'dashboard/api/nucleoWorkOrders';

const valueOf = value => value?.value ?? value;

export function useNucleoWorkOrderDetail({ listingId, orderId, notify, t }) {
  const detail = ref(null);
  const selectedServiceId = ref(null);
  const loading = ref(false);
  const saving = ref(false);
  const error = ref('');

  const selectedService = computed(() =>
    detail.value?.servicos?.find(
      service => String(service.id) === String(selectedServiceId.value)
    )
  );

  const applyDetail = payload => {
    detail.value = payload;
    if (
      !selectedServiceId.value ||
      !payload.servicos?.some(service => service.id === selectedServiceId.value)
    ) {
      selectedServiceId.value = payload.id;
    }
  };

  const load = async () => {
    if (!valueOf(listingId) || !valueOf(orderId)) return;

    loading.value = true;
    error.value = '';
    try {
      const response = await NucleoWorkOrdersAPI.detail(
        valueOf(listingId),
        valueOf(orderId)
      );
      applyDetail(response.data.data);
    } catch (loadError) {
      error.value = t('NUCLEO_WORK_ORDERS.DETAIL.ERRORS.LOAD');
    } finally {
      loading.value = false;
    }
  };

  const actionRequest = (action, serviceId, payload) => {
    const listing = valueOf(listingId);
    if (action === 'schedule')
      return NucleoWorkOrdersAPI.schedule(listing, serviceId, payload);
    if (action === 'start')
      return NucleoWorkOrdersAPI.start(listing, serviceId, payload);
    if (action === 'record_evidence')
      return NucleoWorkOrdersAPI.addEvidence(listing, serviceId, payload);
    if (action === 'complete')
      return NucleoWorkOrdersAPI.complete(listing, serviceId, payload);
    return NucleoWorkOrdersAPI.accept(listing, serviceId, payload);
  };

  const serviceFromDetail = serviceId =>
    detail.value?.servicos?.find(
      service => String(service.id) === String(serviceId)
    );

  const applyActionResponse = response => {
    applyDetail(response.data.data);
    return response.data.data;
  };

  const runServiceExecution = async ({
    service,
    mode,
    scheduled_for,
    observacao,
    files = [],
    document_artifact_ids: documentArtifactIds = [],
  }) => {
    saving.value = true;
    error.value = '';
    try {
      let current = serviceFromDetail(service.id) || service;
      if (mode === 'complete') {
        applyActionResponse(
          await NucleoWorkOrdersAPI.complete(valueOf(listingId), service.id, {
            observacao,
          })
        );
      } else {
        applyActionResponse(
          await NucleoWorkOrdersAPI.schedule(valueOf(listingId), service.id, {
            scheduled_for,
            observacao,
          })
        );
        current = serviceFromDetail(service.id) || current;
        if (current.actions?.includes('start')) {
          applyActionResponse(
            await NucleoWorkOrdersAPI.start(valueOf(listingId), service.id)
          );
        }
        current = serviceFromDetail(service.id) || current;
        if (
          (files.length || documentArtifactIds.length) &&
          current.actions?.includes('record_evidence')
        ) {
          applyActionResponse(
            await NucleoWorkOrdersAPI.addEvidence(
              valueOf(listingId),
              service.id,
              { files, document_artifact_ids: documentArtifactIds }
            )
          );
        }
      }
      notify(t('NUCLEO_WORK_ORDERS.DETAIL.ALERTS.SAVED'));
      return true;
    } catch (actionError) {
      error.value =
        actionError?.response?.data?.message ||
        t('NUCLEO_WORK_ORDERS.DETAIL.ERRORS.SAVE');
      notify(error.value);
      return false;
    } finally {
      saving.value = false;
    }
  };

  const runAction = async ({ action, serviceId, ...payload }) => {
    saving.value = true;
    error.value = '';
    try {
      const id = serviceId || detail.value.id;
      const response = await actionRequest(action, id, payload);
      applyDetail(response.data.data);
      notify(t('NUCLEO_WORK_ORDERS.DETAIL.ALERTS.SAVED'));
      return true;
    } catch (actionError) {
      error.value =
        actionError?.response?.data?.message ||
        t('NUCLEO_WORK_ORDERS.DETAIL.ERRORS.SAVE');
      notify(error.value);
      return false;
    } finally {
      saving.value = false;
    }
  };

  watch([listingId, orderId], load, { immediate: true });

  return {
    detail,
    selectedServiceId,
    selectedService,
    loading,
    saving,
    error,
    load,
    runAction,
    runServiceExecution,
  };
}
